import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTrans from '../../hooks/useTrans';

function CaseCard({ c, t }) {
  return (
    <Link href={`/case-studies/${c.slug}`}>
      <a className='ef-card'>
        <div className='ef-card__media'>
          <span className='ef-card__tag'>{c.tag}</span>
          <img src={c.image} alt={c.title} loading='lazy' />
        </div>
        <div className='ef-card__body'>
          <span className='ef-card__industry'>{c.industry}</span>
          <h3 className='ef-card__title'>{c.title}</h3>
          <p className='ef-card__excerpt'>{c.excerpt}</p>
          <div className='ef-card__footer'>
            <span className='read'>
              {t.cardCta}
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                <line x1='5' y1='12' x2='19' y2='12' />
                <polyline points='12 5 19 12 12 19' />
              </svg>
            </span>
          </div>
        </div>
      </a>
    </Link>
  );
}

function FeaturedCase({ c, t }) {
  return (
    <div className='ef-featured'>
      <div className='ef-featured__media'>
        <span className='ef-featured__badge'>{t.featuredBadge}</span>
        <img src={c.image} alt={c.title} />
      </div>
      <div className='ef-featured__body'>
        <span className='ef-featured__industry'>{t.featuredEyebrow}</span>
        <h3>{c.title}</h3>
        <p>{c.excerpt}</p>
        <Link href={`/case-studies/${c.slug}`}>
          <a className='ef-link-arrow'>
            {t.featuredCta}
            <span className='arrow-dot'>
              <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
                <line x1='5' y1='12' x2='19' y2='12' />
                <polyline points='12 5 19 12 12 19' />
              </svg>
            </span>
          </a>
        </Link>
      </div>
    </div>
  );
}

function CaseStudies({ cases }) {
  const router = useRouter();
  const trans = useTrans();
  const t = trans.caseStudies.list;
  const [visible, setVisible] = useState(6);

  useEffect(() => {
    document.body.setAttribute('data-cs-theme', 'dark');
    return () => {
      document.body.removeAttribute('data-cs-theme');
    };
  }, []);

  const list = Array.isArray(cases) ? cases : [];
  const featured = list.find((c) => c.featured) || list[0] || null;
  const rest = featured ? list.filter((c) => c !== featured) : list;
  const shown = rest.slice(0, visible);

  const scrollToAll = (e) => {
    e.preventDefault();
    const el = document.getElementById('all-cases');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-theme='dark'>
      {/* HERO */}
      <section className='ef-hero'>
        <div className='ef-hero__bg'>
          <img src='/assets/img/banner/ai-safety.jpg' alt='' />
        </div>
        <div className='ef-container ef-hero__inner'>
          <div className='ef-hero__copy'>
            <h1>{t.heroTitle}</h1>
            <p className='lede'>{t.heroLede}</p>
            <div className='ef-hero__cta'>
              <button
                type='button'
                className='ef-btn ef-btn--primary ef-btn--lg'
                onClick={scrollToAll}
              >
                {t.ctaPrimary}
                <span className='arrow'>
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                    <line x1='5' y1='12' x2='19' y2='12' />
                    <polyline points='12 5 19 12 12 19' />
                  </svg>
                </span>
              </button>
              <Link href='/contact'>
                <a className='ef-btn ef-btn--on-dark ef-btn--lg'>{t.ctaSecondary}</a>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className='ef-section' style={{ paddingBottom: 48 }}>
          <div className='ef-container'>
            <FeaturedCase c={featured} t={t} />
          </div>
        </section>
      )}

      {/* ALL CASES */}
      <section
        className='ef-section ef-section--soft'
        id='all-cases'
        style={{ paddingTop: 56 }}
      >
        <div className='ef-container'>
          <div className='ef-section__head'>
            <div>
              <p className='ef-section__title'>{t.sectionEyebrow}</p>
              <h2 className='ef-section__h'>{t.sectionTitle}</h2>
            </div>
          </div>

          {list.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '48px 0' }}>
              {t.empty}
            </p>
          ) : (
            <div className='ef-grid'>
              {shown.map((c) => (
                <CaseCard key={c.slug} c={c} t={t} />
              ))}
            </div>
          )}

          {visible < rest.length && (
            <div className='ef-load-more'>
              <button
                type='button'
                className='ef-btn ef-btn--ghost ef-btn--lg'
                onClick={() => setVisible((v) => Math.min(v + 3, rest.length))}
              >
                {t.loadMore}
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <polyline points='6 9 12 15 18 9' />
                </svg>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className='ef-section ef-section--soft' style={{ paddingTop: 48, paddingBottom: 96, marginBottom: 64 }}>
        <div className='ef-container'>
          <div className='ef-cta-banner'>
            <div className='shield'>
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                <path d='M9 12l2 2 4-4' />
              </svg>
            </div>
            <div className='copy'>
              <h3>{t.bannerTitle}</h3>
              <p>{t.bannerDesc}</p>
            </div>
            <Link href='/contact'>
              <a
                className='ef-btn ef-btn--lg'
                style={{ background: '#fff', color: 'var(--ef-blue-primary)' }}
              >
                {t.bannerCta}
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <line x1='5' y1='12' x2='19' y2='12' />
                  <polyline points='12 5 19 12 12 19' />
                </svg>
              </a>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CaseStudies;
